import React from "react";
import { useNavigate } from "react-router-dom";
import PopupBase from "../base/PopupBase";
import CustomButton from "../../components/buttons/CustomButton";
import styles from './AppointmentTypePopup.module.css';
import { Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { getModuleLabel } from '../../pages/appointment/modules/appointmentModules';

/**
 * Escolha do tipo de atendimento (ao iniciar ou para trocar durante a consulta).
 * Lista vertical com rolagem, para clínicas com muitos tipos. `types` vem de
 * CompanyConfig.AppointmentTypes; `lastTypeId` é o tipo do último atendimento
 * deste paciente e aparece como etiqueta na linha, junto com o tipo padrão.
 * O rodapé da lista leva a Cadastros > Tipos de atendimento, onde os tipos são montados.
 */
function AppointmentTypePopup(props) {
    const { types, selectedTypeId, onSelect, onConfirm, onClose, isChangingType, lastTypeId } = props;
    const navigate = useNavigate();

    function goToManageTypes() {
        onClose();
        navigate('/company/appointment-types');
    }

    function getTitle() {
        return isChangingType
            ? "Alterar tipo de atendimento"
            : "Selecione o tipo de atendimento";
    }

    function describeModules(type) {
        return (type.Modules || []).map(getModuleLabel).join(" · ");
    }

    function renderOption(type) {
        const selected = String(type.Id) === String(selectedTypeId);
        const isLast = lastTypeId != null && String(type.Id) === String(lastTypeId);
        return (
            <div
                key={type.Id}
                className={`${styles.option} ${selected ? styles.selected : ''}`}
                onClick={() => onSelect(type.Id)}
                onDoubleClick={() => { onSelect(type.Id); onConfirm(); }}
                role="button"
                aria-pressed={selected}
            >
                <span className={styles.radio}>
                    {selected ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                </span>
                <div className={styles.optionText}>
                    <span className={styles.optionLabel}>{type.Label}</span>
                    <span className={styles.optionModules}>{describeModules(type)}</span>
                </div>
                <div className={styles.optionTags}>
                    {isLast && <Chip label="Último atendimento" size="small" color="primary" variant="outlined" />}
                    {type.IsDefault && <Chip label="Padrão" size="small" variant="outlined" />}
                </div>
            </div>
        );
    }

    function getContent() {
        return (
            <div className={styles.container}>
                <span className={styles.hint}>
                    {isChangingType
                        ? "Os cards da tela seguem os módulos do tipo escolhido. Nada registrado é perdido."
                        : "O tipo define quais cards aparecem neste atendimento. Dê dois cliques para iniciar direto."}
                </span>
                <div className={styles.list}>
                    {(types || []).map(renderOption)}
                </div>
                <div className={styles.manage}>
                    <div className={styles.manageText}>
                        <span className={styles.manageTitle}>Monte seus atendimentos</span>
                        <span className={styles.manageHint}>
                            Crie novos tipos ou mude os módulos de cada um em Cadastros &gt; Tipos de atendimento.
                        </span>
                    </div>
                    <CustomButton
                        variant="outlined"
                        style="secondary"
                        icon={<TuneOutlinedIcon />}
                        label="Gerenciar tipos"
                        onClick={goToManageTypes}
                    />
                </div>
            </div>
        );
    }

    function getFooter() {
        return (
            <div className={styles.footer}>
                <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="Cancelar"
                    onClick={onClose}
                />
                <CustomButton
                    variant="contained"
                    style="primary"
                    label={isChangingType ? "Confirmar alteração" : "Iniciar atendimento"}
                    onClick={onConfirm}
                    enabled={selectedTypeId != null}
                    data-tour={isChangingType ? undefined : 'appointment-type-confirm'}
                />
            </div>
        );
    }

    return (
        <PopupBase
            Title={getTitle()}
            Content={getContent()}
            Footer={getFooter()}
            OnClose={onClose}
        />
    );
}

export default AppointmentTypePopup;
